import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Content } from '../components/Content';
import { Documents } from '../components/Documents';
import { SubmitButton } from '../components/forms/SubmitButton';
import { InsuranceConsent } from '../components/InsuranceConsent';
import { InsuranceHeader } from '../components/InsuranceHeader';
import {
  InsuranceProductStatus,
  Status,
} from '../components/InsuranceProductStatus';
import { InsuranceQuizTable } from '../components/InsuranceQuizTable';
import { Markdown } from '../components/Markdown';
import { Account } from '../hooks/useAccount';
import { Auth } from '../hooks/useAuth';
import { Insurance as InsuranceHook } from '../hooks/useInsurance';
import { useQuiz } from '../hooks/useQuiz';
import { useRouter } from '../hooks/useRouter';
import { useRoutes } from '../hooks/useRoutes';
import { Settings } from '../hooks/useSettings';
import bankidLogo from '../public/bankid_white.svg';
import { theme } from '../styles/theme';
import { Person } from '../swagger';
import { Page } from '../types';
import { api, getAuthHeaders } from '../utils/api';
import { clearPersonalNumber } from '../utils/clearPersonalNumber';
import { nextPageAfterQuiz } from '../utils/nextPageAfterQuiz';
import { isResponse, reportError } from '../utils/reportError';
import { validatePersonalNumber } from '../utils/validatePersonalNumber';

/** Why the engine refuses a child's declaration (`guardianSignBlocker`). */
const SIGN_ERRORS = [
  'not_the_guardian',
  'invite_expired',
  'child_adult',
  'package_has_partner',
];
import TypeEnum = Person.TypeEnum;

export const Coinsurance: Page = () => {
  const { id } = useParams<{ id: string }>();
  const { settings } = Settings.useContainer();
  const { account, refreshAccount } = Account.useContainer();
  const { startAuth, status: authStatus } = Auth.useContainer();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const currentPersonalnumber = account?.sensitive?.personnummer;
  const { history } = useRouter();
  const { mina } = useRoutes();

  const {
    quiz,
    load,
    answerQuestion,
    giveConsent,
    isDone,
    reset,
  } = useQuiz();
  const [person, setPerson] = React.useState<Person>({ type: TypeEnum.Main });
  const clearPNO = clearPersonalNumber(person.personnummer);
  const isSamePNO = currentPersonalnumber === clearPNO;
  const completed = isDone && typeof quiz.age === 'number';
  const { formatMessage } = useIntl();
  // A child's declaration: the parent answers for the child and signs with
  // their own BankID, so the personnummer asked for is the parent's.
  // Only `GET /quiz/:id` names the child; every autosave answers without it,
  // so the page keeps what the load said rather than reading `quiz`.
  const [invitedChild, setInvitedChild] = React.useState<string>();
  const isForChild = typeof invitedChild === 'string';
  const childName =
    invitedChild || formatMessage({ id: 'admin.add_child.the_child' });
  const [signerPNOInvalid, setSignerPNOInvalid] = React.useState(false);
  const [signError, setSignError] = React.useState<string>();

  // A parent who is already signed in signs as themselves.
  React.useEffect(() => {
    if (isForChild && currentPersonalnumber) {
      setPerson((current) =>
        current.personnummer
          ? current
          : { ...current, personnummer: currentPersonalnumber },
      );
    }
  }, [isForChild, currentPersonalnumber]);

  // Functional, like every write to `person` here: the effects run in one
  // flush, and a stale copy would drop what another just wrote.
  React.useEffect(() => {
    setPerson((current) =>
      current.quiz === quiz ? current : { ...current, quiz },
    );
  }, [quiz]);

  React.useEffect(() => {
    setLoading(true);
    // A new link is a new declaration: nothing of the last one's child.
    setInvitedChild(undefined);
    load(id)
      // Functional: a personnummer filled in meanwhile (the signed-in
      // parent's, below) must survive the load settling.
      .then((loaded) => {
        setInvitedChild(loaded.childName);
        setPerson((current) => ({ ...current, quiz: loaded }));
      })
      // An unknown or expired link (404 after 90 days): the page shows no
      // declaration, as before; nothing to report.
      .catch(() => undefined)
      .finally(() => setLoading(false));
    // Loads the quiz for this id. `load` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * A signed declaration may be refused: a child's when signed by someone
   * other than the parent it was sent to, expired, or the child has turned
   * 18; anyone's when the package already covers two adults. The engine then
   * leaves it open and says which; the page says so instead of thanking.
   */
  const signDeclaration = async (): Promise<boolean> => {
    setSignError(undefined);

    try {
      reset(
        await api.quizControllerSave(true, quiz, { headers: getAuthHeaders() }),
      );
      return true;
    } catch (e) {
      let code = '';
      try {
        code = isResponse(e) ? String((await e.json()).message ?? '') : '';
      } catch {
        // Not a JSON answer: the generic message below.
      }
      setSignError(SIGN_ERRORS.includes(code) ? code : 'generic');
      if (!SIGN_ERRORS.includes(code)) {
        reportError(e);
      }
      return false;
    }
  };

  const complete = async () => {
    setSubmitting(true);

    if (clearPNO && !isSamePNO) {
      await startAuth({ personnummer: clearPNO });
    }
  };

  React.useEffect(() => {
    if (submitting && isSamePNO) {
      const save = signDeclaration();

      save.then((signed) => {
        setSubmitting(false);

        if (!signed) {
          return;
        }

        refreshAccount().then((acc) => {
          const next = nextPageAfterQuiz(acc, mina);

          if (next) {
            history.push(next);
          }
        });
      });
    }
    // Fires once when the customer submits. Everything else here is rebuilt
    // every render; depending on any of it would save the quiz again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, isSamePNO]);

  React.useEffect(() => {
    if (
      submitting &&
      !authStatus &&
      !currentPersonalnumber &&
      !settings.token
    ) {
      setSubmitting(false);
    }
  }, [submitting, authStatus, currentPersonalnumber, settings.token]);

  return (
    <InsuranceHook.Provider>
      <InsuranceHeader />
      <Content>
        {isForChild ? (
          <Markdown
            children="coinsurance.child.text"
            values={{ name: childName }}
          />
        ) : (
          <Markdown children="coinsurance.text" />
        )}
        <Box pb={4}>
          {!!quiz.id && !loading && (
            <>
              {!completed && isForChild && (
                <>
                  {/* The label sits beside the field, as on every other row
                      of the declaration; the theme's outlined field has no
                      room for a floating one. */}
                  <Box
                    pb={2}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ gap: 16 }}
                  >
                    <label htmlFor="coinsurance-signer-personnummer">
                      <Typography variant="body1" component="span">
                        {formatMessage({
                          id: 'coinsurance.child.personnummer',
                        })}
                      </Typography>
                    </label>
                    <TextField
                      variant="outlined"
                      size="small"
                      type="tel"
                      name="personnummer"
                      id="coinsurance-signer-personnummer"
                      placeholder="ÅÅÅÅMMDD-XXXX"
                      error={signerPNOInvalid}
                      value={person.personnummer || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSignerPNOInvalid(
                          !!value && !validatePersonalNumber(value),
                        );
                        setPerson({ ...person, personnummer: value });
                      }}
                      style={{ width: '14em' }}
                    />
                  </Box>
                  <InsuranceQuizTable
                    person={{ ...person, type: TypeEnum.Child }}
                    onAnswer={answerQuestion}
                    hideDetails
                  />
                </>
              )}
              {!completed && !isForChild && (
                <InsuranceQuizTable
                  person={person}
                  onAnswer={answerQuestion}
                  onPerson={(p) => setPerson({ ...p })}
                />
              )}
              <InsuranceConsent
                labels={[
                  'insurance_consent1',
                  'insurance_consent2',
                  'insurance_consent3',
                  'insurance_consent5',
                ]}
                consent={Boolean(quiz.needResult)}
                onConsent={giveConsent}
              />
              {signError && (
                <Box pb={2}>
                  <Alert severity="error">
                    {formatMessage({
                      id: `coinsurance.child.error.${signError}`,
                    })}
                  </Alert>
                </Box>
              )}
              <InsuranceProductStatus
                person={person}
                isInvite
                forChild={isForChild}
              >
                {(status) => {
                  const disabled =
                    status !== Status.SUCCESS ||
                    completed ||
                    !clearPNO ||
                    !quiz.needResult;

                  if (completed) {
                    return (
                      <Box pt={2}>
                        <Markdown
                          children={
                            isForChild
                              ? 'quiz.completed.child'
                              : 'quiz.completed'
                          }
                          values={{ name: childName }}
                          style={{ color: theme.palette.text.primary }}
                        />
                      </Box>
                    );
                  }

                  return (
                    <>
                      <SubmitButton
                        label="save_quiz"
                        disabled={disabled}
                        loading={submitting}
                        onClick={complete}
                        endIcon={
                          !isSamePNO ? (
                            <img
                              src={bankidLogo}
                              alt="BankID"
                              width={24}
                              height={24}
                              className="invert-disabled"
                            />
                          ) : undefined
                        }
                      />
                    </>
                  );
                }}
              </InsuranceProductStatus>
            </>
          )}
        </Box>
        <Box pt={2} pl={3} pr={3}>
          <Documents />
        </Box>
      </Content>
    </InsuranceHook.Provider>
  );
};

Coinsurance.page = 'coinsurance';
